import { useContext, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { AutoReportDataType } from '@oy/types';
import { toast } from '@oy/uidesktop';
import { isEqual } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { autoReportPostConfig } from 'src/clients';
import { AutoReportContext } from 'src/context';
import { useTranslate, useUserRole } from 'src/hooks';
import { dateBE } from 'src/utilities/day';
import * as yup from 'yup';
import { useAutoReportData } from '../../hooks';
import { generateYupSchema } from './helper';
import {
  useAutoReportFormData,
  useAutoReportFormDataConfig,
} from './useAutoReportFormData';

export const useAutoReportSideModalImpl = ({
  onParentClose,
}: {
  onParentClose?: () => void;
}) => {
  const { isSuperAdmin, isAdmin } = useUserRole();

  const {
    isEditConfig,
    selectedProduct,
    showSideModal,
    forceEnableField,
    isActive,
    showCancelReportModal,
    isErrorMessageVisible,
    clickFrom,
    onCloseSideModal,
    setIsActive,
    setIsErrorMessageVisible,
    onToggleActivation,
    onShowDisableReportModalFromProduct,
    onDisable,
    onCloseDisableReportModal,
  } = useContext(AutoReportContext);
  const { mutate } = useAutoReportData(isSuperAdmin || isAdmin);
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);

  const {
    data,
    mutate: mutateField,
    isLoading: isLoadingField,
    isValidating: isValidatingField,
  } = useAutoReportFormData(selectedProduct);

  const {
    data: currentValue,
    isLoading: isLoadingValue,
    mutate: mutateValue,
    isValidating: isValidatingValue,
  } = useAutoReportFormDataConfig(isEditConfig, selectedProduct);

  const checkIsActive = () => {
    const element = currentValue?.find(
      (element: AutoReportDataType) => element.parameter === 'IS_ACTIVE',
    );
    return element?.value?.toLowerCase() === 'true';
  };

  useEffect(() => {
    setIsActive(checkIsActive());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading =
    isSubmitting ||
    isLoadingField ||
    isValidatingField ||
    isLoadingValue ||
    isValidatingValue;

  const { getTranslation } = useTranslate();

  const schema = useMemo(() => generateYupSchema(data ?? []), [data]);

  type SchemaType = yup.InferType<typeof schema>;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<SchemaType>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const currentValueToForm = useMemo(() => {
    if (!currentValue || !schema || Object.keys(schema.fields).length === 0) {
      return null;
    }
    const result = currentValue.reduce((acc: { [key: string]: any }, item) => {
      acc[item.parameter] = item.value;
      if (schema.fields[item.parameter] instanceof yup.ArraySchema) {
        acc[item.parameter] = item.value?.split(';');
      }
      if (schema.fields[item.parameter] instanceof yup.DateSchema) {
        acc[item.parameter] = dateBE(item.value);
      }
      return acc;
    }, {});
    return result;
  }, [currentValue, schema]);

  useEffect(() => {
    if (showSideModal && !isEditConfig) {
      reset({});
    }
    if (showSideModal && isEditConfig && currentValueToForm) {
      reset(currentValueToForm);
    }
  }, [reset, currentValueToForm, isEditConfig, showSideModal]);

  useEffect(() => {
    if (selectedProduct) {
      mutateField();
      mutateValue();
    }
  }, [mutateField, mutateValue, showSideModal, selectedProduct]);

  const onSubmit = () => {
    if (selectedProduct && !isActive && !forceEnableField) {
      onShowDisableReportModalFromProduct();
      reset({});
    } else {
      setIsErrorMessageVisible(true);
      handleSubmit(async data => {
        const transformedArray = Object.entries(data).map(([key, value]) => {
          let newValue = value;
          if (Array.isArray(value)) {
            newValue = value.join(';');
          }
          return { parameter: key, value: newValue };
        });

        if (schema?.fields?.DESCRIPTION && !data.DESCRIPTION) {
          transformedArray.push({ parameter: 'DESCRIPTION', value: '' });
        }

        try {
          setIsSubmitting(true);
          const response = await autoReportPostConfig({
            product_name: selectedProduct!,
            configs: [
              ...transformedArray.filter(
                item => item.parameter !== 'IS_ACTIVE',
              ),
              { parameter: 'IS_ACTIVE', value: true },
            ],
          });

          if (response.data.success) {
            if (isEditConfig) {
              AnalyticsManager.instance().track(
                AnalyticsEvent.auto_report_edit_config_success,
                {
                  product: selectedProduct?.toLowerCase(),
                  click_from: clickFrom,
                },
              );
              toast.success(getTranslation('settings_autoReport_saveConfig'));
            } else {
              AnalyticsManager.instance().track(
                AnalyticsEvent.auto_report_activate_success,
                {
                  product: selectedProduct?.toLowerCase(),
                  click_from: clickFrom,
                },
              );
              toast.success(getTranslation('settings_autoReport_active'));
            }
            mutate();
            onCloseSideModal();
            return;
          }
          toast.error(
            getTranslation('settings_autoReport_message_failedSaveData'),
          );
        } catch (e) {
          toast.error(
            getTranslation('settings_autoReport_message_failedSaveData'),
          );
        } finally {
          setIsSubmitting(false);
        }
      })();
    }
  };

  const isFieldEdited = (fieldName: string) => {
    const fieldValue = watch(fieldName);
    const isEdited = !isEqual(fieldValue, currentValueToForm?.[fieldName]);

    return isEdited;
  };

  const isAnyFieldEdited = () => {
    if (!currentValueToForm) {
      return true;
    }
    return Object.keys(currentValueToForm).some(fieldName =>
      isFieldEdited(fieldName),
    );
  };

  const isFieldDisable = !isActive && checkIsActive();
  const isToggleOnSideModalStillInactive =
    !isActive && !forceEnableField && !checkIsActive();
  const isSubmitDisabled =
    ((!isAnyFieldEdited() || !isDirty) && !isFieldDisable) ||
    isToggleOnSideModalStillInactive;

  const onClose = () => {
    reset({});
    onCloseCancelModal();
    onCloseSideModal();
    if (onParentClose) {
      onParentClose();
    }
  };

  const onShowCancelModal = () => {
    setIsShowCancelModal(true);
  };

  const onCloseCancelModal = () => {
    setIsShowCancelModal(false);
  };

  const state = {
    showSideModal,
    schema,
    control,
    isShowCancelModal,
    data,
    isLoading,
    selectedProduct,
    isSubmitDisabled,
    isActive,
    forceEnableField,
    isFieldDisable,
    showCancelReportModal,
    isErrorMessageVisible,
  };

  return {
    state,
    onCloseSideModal,
    onSubmit,
    onClose,
    onShowCancelModal,
    onCloseCancelModal,
    onToggleActivation,
    onDisable,
    onCloseDisableReportModal,
  };
};
